import * as yup from 'yup';
import { createInvoiceItemSchema } from './invoiceItem.subschema';
import { InvoiceType, InvoiceStatus } from '../Types/invoicesTypes';
import { validatePolishNip } from '../utils/nipValidator';
import { invoiceLogger } from '../utils/invoiceLogger';
import {
  INVOICE_DATE_FORMAT_ERROR_MESSAGE,
  isValidInvoiceApiDate,
  normalizeInvoiceDateInput,
} from './dateValidation';

export const createInvoiceSchema = yup.object({
  // Podstawowe dane
  reservationId: yup.number().optional(),
  companyId: yup
    .number()
    .nullable()
    .when('reservationId', {
      is: (val: number | null | undefined) => !val, // Jeśli BRAK rezerwacji
      then: (schema) =>
        schema
          .required('Firma jest wymagana dla faktur bez rezerwacji')
          .min(1, 'Wybierz firmę z listy'),
      otherwise: (schema) => schema.nullable(), // Z rezerwacją - opcjonalne (auto-set)
    }),
  currency: yup.string().required('Wybierz walutę'),
  scheme: yup.mixed().required('Wybierz schemat faktury'),
  type: yup.mixed().required('Wybierz typ faktury'),

  // Dane dokumentu
  issueDate: yup
    .string()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .required('Data wystawienia jest wymagana')
    .test('issue-date-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
      isValidInvoiceApiDate(value)
    ),
  saleDate: yup
    .string()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .required('Data wykonania usługi jest wymagana')
    .test('sale-date-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
      isValidInvoiceApiDate(value)
    ),
  placeOfIssue: yup
    .string()
    .required('Miejsce wystawienia jest wymagane')
    .min(2, 'Miejsce wystawienia musi mieć min. 2 znaki')
    .max(100, 'Miejsce wystawienia może mieć max. 100 znaków'),
  paymentMethod: yup.mixed().required('Wybierz formę płatności'),
  bankAccount: yup
    .string()
    .required('Konto bankowe jest wymagane')
    .min(10, 'Numer konta musi mieć min. 10 znaków'),
  paymentDueDate: yup
    .string()
    .nullable()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .test('payment-due-date-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
      isValidInvoiceApiDate(value)
    ),

  // Sprzedawca (wymagane przez backend)
  sellerName: yup
    .string()
    .required('Nazwa sprzedawcy jest wymagana')
    .min(2, 'Nazwa sprzedawcy musi mieć min. 2 znaki'),
  sellerStreetNo: yup
    .string()
    .required('Ulica i numer sprzedawcy są wymagane')
    .min(2, 'Ulica i numer muszą mieć min. 2 znaki'),
  sellerAddress: yup
    .string()
    .required('Adres sprzedawcy (kod, miasto) jest wymagany')
    .min(2, 'Adres musi mieć min. 2 znaki'),
  sellerNip: yup
    .string()
    .required('NIP sprzedawcy jest wymagany')
    .test('nip-format', 'Nieprawidłowy format NIP', function (value) {
      if (!value) {
        return this.createError({ message: 'NIP sprzedawcy jest wymagany' });
      }

      // Clean NIP - remove dashes and spaces
      const cleaned = value.replace(/[-\s]/g, '');

      // Must be exactly 10 digits (Polish NIP format)
      if (!/^\d{10}$/.test(cleaned)) {
        return this.createError({
          message: 'NIP musi mieć 10 cyfr (możesz użyć myślników, np. 123-456-78-90)',
        });
      }

      // Validate checksum using MOD-11 algorithm
      if (!validatePolishNip(value)) {
        return this.createError({
          message: 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.',
        });
      }

      return true;
    }),

  // Pozycje faktury (wymagane przez backend)
  items: yup
    .array()
    .of(createInvoiceItemSchema())
    .min(1, 'Dodaj przynajmniej jedną pozycję faktury')
    .required('Lista pozycji jest wymagana')
    .when('type', {
      is: (val: any) => val === InvoiceType.Correction || val === 'correction',
      then: (schema) => schema.of(createInvoiceItemSchema({ allowZeroQuantity: true })),
      otherwise: (schema) => schema.of(createInvoiceItemSchema()),
    })
    .when('type', {
      is: (val: any) => val === InvoiceType.Prepayment || val === 'prepayment' || val === 1,
      then: (schema) =>
        schema.test(
          'total-matches-paid',
          'Suma brutto (z VAT) pozycji faktury musi być równa kwocie zapłaconej (paidAmount)',
          function (items) {
            const { paidAmount } = this.parent;

            invoiceLogger.debug('═══════════════════════════════════════════════════════════════');
            invoiceLogger.debug('[Validation] 🔍 YUP VALIDATION - Prepayment amount match');
            invoiceLogger.debug('═══════════════════════════════════════════════════════════════');
            invoiceLogger.debug('[Validation] Input:', {
              paidAmount,
              itemsCount: items?.length || 0,
              items: items?.map((item: any, idx: number) => ({
                index: idx,
                name: item.name,
                unitNet: item.unitNet,
                unitGross: item.unitGross, // ✅ Log unitGross
                quantity: item.quantity,
                vatType: item.vatType,
              })),
            });

            if (!paidAmount || !items || items.length === 0) {
              invoiceLogger.warn('[Validation] ⚠️  Early return - missing paidAmount or items');
              return true;
            }

            // ✅ FIX: For Prepayment, use unitGross from form (user enters gross amounts)
            // Backend will calculate unitNet from unitGross, so frontend validation should use unitGross
            const totalGross = items.reduce((sum: number, item: any, idx: number) => {
              const unitGross = item.unitGross || 0; // ✅ Read unitGross from form
              const quantity = item.quantity || 1;
              const itemGross = unitGross * quantity;

              invoiceLogger.debug(`[Validation] Item ${idx}:`, {
                unitGross,
                quantity,
                itemGross: itemGross.toFixed(2),
              });

              return sum + itemGross;
            }, 0);

            invoiceLogger.debug('[Validation] 📊 Totals:', {
              totalGross: totalGross.toFixed(2),
              paidAmount: paidAmount.toFixed(2),
            });

            const diff = Math.abs(totalGross - paidAmount);
            // Allow 0.01 tolerance for floating point precision
            if (diff > 0.01) {
              invoiceLogger.error(
                '═══════════════════════════════════════════════════════════════'
              );
              invoiceLogger.error('[Validation] ❌ VALIDATION FAILED:', {
                totalGross: totalGross.toFixed(2),
                paidAmount: paidAmount.toFixed(2),
                diff: diff.toFixed(2),
              });
              invoiceLogger.error(
                '═══════════════════════════════════════════════════════════════'
              );

              return this.createError({
                message: `Suma brutto pozycji (${totalGross.toFixed(
                  2
                )} PLN) nie zgadza się z kwotą zapłaconą (${paidAmount.toFixed(
                  2
                )} PLN). Różnica: ${diff.toFixed(2)} PLN`,
              });
            }

            invoiceLogger.debug('[Validation] ✅ Validation PASSED - amounts match!');
            invoiceLogger.debug('═══════════════════════════════════════════════════════════════');
            return true;
          }
        ),
      otherwise: (schema) => schema,
    }),

  // Nabywca (wymagane przez backend)
  buyerName: yup
    .string()
    .required('Nazwa nabywcy jest wymagana')
    .min(2, 'Nazwa nabywcy musi mieć min. 2 znaki'),
  buyerStreetNo: yup
    .string()
    .required('Ulica i numer nabywcy są wymagane')
    .min(2, 'Ulica i numer muszą mieć min. 2 znaki'),
  buyerAddress: yup
    .string()
    .required('Adres nabywcy (kod, miasto) jest wymagany')
    .min(2, 'Adres musi mieć min. 2 znaki'),
  buyerNip: yup
    .string()
    .nullable()
    .test('nip-format-optional', 'Nieprawidłowy format NIP', function (value) {
      // Empty/null is valid (optional field)
      if (!value || value.trim() === '') return true;

      const cleaned = value.replace(/[-\s]/g, '');
      const buyerCountryIso = String(this.parent?.buyerCountryIso || 'PL').toUpperCase();

      if (buyerCountryIso === 'PL') {
        if (/^PL/i.test(cleaned)) {
          return this.createError({
            message: 'Dla kraju PL wpisz NIP bez prefiksu kraju (np. 1234567890).',
          });
        }

        if (!/^\d{10}$/.test(cleaned)) {
          return this.createError({
            message: 'Dla kraju PL NIP musi mieć dokładnie 10 cyfr.',
          });
        }

        if (!validatePolishNip(cleaned)) {
          return this.createError({
            message: 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.',
          });
        }

        return true;
      }

      // Check if it's Polish NIP (10 digits)
      if (/^\d{10}$/.test(cleaned)) {
        // Validate checksum using MOD-11 algorithm
        if (!validatePolishNip(cleaned)) {
          return this.createError({
            message: 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.',
          });
        }
        return true;
      }

      // Check if it's EU VAT number (2 letters + 8-12 digits)
      if (/^[A-Z]{2}\d{8,12}$/i.test(cleaned)) {
        return true; // EU VAT format valid
      }

      // Invalid format
      return this.createError({
        message:
          'NIP musi mieć 10 cyfr (możesz użyć myślników) lub format VAT-UE (np. DE123456789)',
      });
    }),
  buyerCountryIso: yup
    .string()
    .required('Kod kraju nabywcy jest wymagany')
    .length(2, 'Kod kraju nabywcy musi mieć 2 znaki')
    .matches(/^[A-Za-z]{2}$/, 'Kod kraju nabywcy musi zawierać tylko litery'),
  isBuyerVatEu: yup.boolean().required('Informacja o statusie VAT-UE nabywcy jest wymagana'),
  buyerIsJst: yup.boolean().required('Informacja o statusie JST nabywcy jest wymagana'),

  // Odbiorca (opcjonalny, ale warunkowo wymagany)
  useDifferentRecipient: yup.boolean(),
  recipientName: yup.string().when('useDifferentRecipient', {
    is: true,
    then: (schema) =>
      schema
        .required('Nazwa odbiorcy jest wymagana')
        .min(2, 'Nazwa odbiorcy musi mieć min. 2 znaki'),
    otherwise: (schema) => schema.nullable(),
  }),
  recipientStreetNo: yup.string().when('useDifferentRecipient', {
    is: true,
    then: (schema) =>
      schema
        .required('Ulica i numer odbiorcy są wymagane')
        .min(2, 'Ulica i numer muszą mieć min. 2 znaki'),
    otherwise: (schema) => schema.nullable(),
  }),
  recipientAddress: yup.string().when('useDifferentRecipient', {
    is: true,
    then: (schema) =>
      schema
        .required('Adres odbiorcy (kod, miasto) jest wymagany')
        .min(2, 'Adres musi mieć min. 2 znaki'),
    otherwise: (schema) => schema.nullable(),
  }),
  recipientNip: yup
    .string()
    .nullable()
    .test('nip-format-optional', 'Nieprawidłowy format NIP', function (value) {
      // Empty/null is valid (optional field)
      if (!value || value.trim() === '') return true;

      const cleaned = value.replace(/[-\s]/g, '');

      // Check if it's Polish NIP (10 digits)
      if (/^\d{10}$/.test(cleaned)) {
        // Validate checksum using MOD-11 algorithm
        if (!validatePolishNip(value)) {
          return this.createError({
            message: 'NIP zawiera błędną sumę kontrolną. Sprawdź poprawność numeru.',
          });
        }
        return true;
      }

      // Check if it's EU VAT number (2 letters + 8-12 digits)
      if (/^[A-Z]{2}\d{8,12}$/i.test(cleaned)) {
        return true; // EU VAT format valid
      }

      // Invalid format
      return this.createError({
        message:
          'NIP musi mieć 10 cyfr (możesz użyć myślników) lub format VAT-UE (np. DE123456789)',
      });
    }),

  // PFRON Refund fields (optional, validated only when Type = Refund)
  refundParticipantReferralCost: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'Koszt nie może być ujemny')
    .when('type', {
      is: 'Refund',
      then: (schema) => schema.required('Pole wymagane dla faktur refundacyjnych'),
    }),

  refundGuardianReferralCost: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'Koszt nie może być ujemny'),

  refundPfronSubsidyParticipant: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'Dofinansowanie nie może być ujemne')
    .when('type', {
      is: 'Refund',
      then: (schema) => schema.required('Pole wymagane dla faktur refundacyjnych'),
    }),

  refundPfronSubsidyGuardian: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'Dofinansowanie nie może być ujemne'),

  refundOwnContribution: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'Dopłata nie może być ujemna'),

  // Payment fields (required for Prepayment invoices)
  status: yup.mixed().when('type', {
    is: (val: any) => val === InvoiceType.Prepayment || val === 'prepayment' || val === 1,
    then: (schema) =>
      schema
        .required('Status jest wymagany dla faktury zaliczkowej')
        .test('is-paid', 'Faktura zaliczkowa musi mieć status "Opłacona" (Paid)', function (value) {
          if (value === InvoiceStatus.Paid || value === 'paid' || value === 2) {
            return true;
          }
          return this.createError({
            message: 'Faktura zaliczkowa musi mieć status "Opłacona" (Paid)',
          });
        }),
    otherwise: (schema) => schema.nullable(),
  }),

  paidAmount: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('type', {
      is: (val: any) => val === InvoiceType.Prepayment || val === 'prepayment' || val === 1,
      then: (schema) =>
        schema
          .required('Kwota zapłacona jest wymagana dla faktury zaliczkowej')
          .min(0.01, 'Kwota musi być większa od 0'),
      otherwise: (schema) => schema.nullable(),
    }),

  paidAt: yup
    .string()
    .nullable()
    .transform((_value, originalValue) => normalizeInvoiceDateInput(originalValue) as string)
    .when('type', {
      is: (val: any) => val === InvoiceType.Prepayment || val === 'prepayment' || val === 1,
      then: (schema) =>
        schema
          .required('Data zapłaty jest wymagana dla faktury zaliczkowej')
          .test('paid-at-format', INVOICE_DATE_FORMAT_ERROR_MESSAGE, (value) =>
            isValidInvoiceApiDate(value)
          ),
      otherwise: (schema) => schema.nullable(),
    }),
});
